import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import useGetMutationFn from '../use-get-mutation-fn';
import { isStoreScopedKey } from './stores.query';
import { getApiErrorCode, getApiErrorMessage } from '@/client/utils/errors';
import type { StoreListingModeration, StoreSettingsRow } from '@/client/types/stores';

const BASE = '/api/admin/stores';

/**
 * Refresh every view of one store after a write, plus the stores list. A
 * moderation action changes flags the Overview, Settings, Plan, Payout and
 * Audit tabs all read, so invalidating a hand-picked subset left tabs stale.
 */
const useStoreInvalidator = () => {
  const qc = useQueryClient();
  return (storeid: number) =>
    qc.invalidateQueries({ predicate: (q) => isStoreScopedKey(q.queryKey, storeid) });
};

/**
 * Wrap a store mutation. The full payload (incl. `storeid`) is sent as the body;
 * the backend reads only the fields it declares and ignores the extra `storeid`.
 * Every `/api/admin/stores/*` write is JSON-allowlisted in the proxy
 * (`src/server/lib/utils.ts`), DELETE included, so `reason` reaches the audit log.
 */
export function useStoreAction<P extends { storeid: number }>(
  buildEndpoint: (p: P) => string,
  method: 'PUT' | 'POST' | 'DELETE' = 'PUT',
  successMessage?: string | ((p: P) => string),
) {
  const invalidate = useStoreInvalidator();
  const fn = useGetMutationFn<P>({ endpoint: (p) => buildEndpoint(p), method });
  return useMutation({
    mutationFn: fn,
    onSuccess: (_data, vars) => {
      invalidate(vars.storeid);
      const msg = typeof successMessage === 'function' ? successMessage(vars) : successMessage;
      if (msg) toast.success(msg);
    },
    onError: (err) => {
      const code = getApiErrorCode(err);
      toast.error(
        code === 'payment_not_collected' ? 'Confirm that the payment was collected before marking this order delivered.'
          : code === 'payment_pending' ? 'The buyer has not paid the invoice yet, so this order cannot be confirmed.'
          : getApiErrorMessage(err, 'The store action failed'),
      );
    },
  });
}

type WithReason = { storeid: number; reason?: string };

// ── Moderation ─────────────────────────────────────────────────────────────

export const useSuspendStore = () =>
  useStoreAction<{ storeid: number; reason: string }>((p) => `${BASE}/${p.storeid}/suspend`, 'PUT', 'Store suspended');

export const useUnsuspendStore = () =>
  useStoreAction<WithReason>((p) => `${BASE}/${p.storeid}/suspend`, 'DELETE', 'Suspension lifted');

export const useVerifyStore = () =>
  useStoreAction<{ storeid: number; verified: boolean; reason?: string }>(
    (p) => `${BASE}/${p.storeid}/verify`, 'PUT',
    (p) => (p.verified ? 'Store verified' : 'Verification removed'),
  );

export const useFeatureStore = () =>
  useStoreAction<{ storeid: number; featured: boolean; reason?: string }>(
    (p) => `${BASE}/${p.storeid}/feature`, 'PUT',
    (p) => (p.featured ? 'Store featured' : 'Store unfeatured'),
  );

export const usePublishStore = () =>
  useStoreAction<{ storeid: number; published: boolean; reason?: string }>(
    (p) => `${BASE}/${p.storeid}/publish`, 'PUT',
    (p) => (p.published ? 'Store published' : 'Store unpublished'),
  );

export const useSetStoreTester = () =>
  useStoreAction<{ storeid: number; tester: boolean; reason: string }>(
    (p) => `${BASE}/${p.storeid}/tester`, 'PUT',
    (p) => (p.tester ? 'Marked as test store' : 'Marked as live store'),
  );

export const useReviewStore = () =>
  useStoreAction<{ storeid: number; review: boolean; review_reason?: string; reason?: string }>(
    (p) => `${BASE}/${p.storeid}/review`, 'PUT',
    (p) => (p.review ? 'Review hold placed' : 'Review hold cleared'),
  );

// ── Lifecycle (danger zone) ────────────────────────────────────────────────

/** Force-close: delists every listing; the seller cannot re-open. */
export const useCloseStore = () =>
  useStoreAction<{ storeid: number; reason: string }>((p) => `${BASE}/${p.storeid}/close`, 'POST', 'Store force-closed');

/** Clear a force-close and re-open through the seller's lifecycle rules. */
export const useOpenStore = () =>
  useStoreAction<WithReason>((p) => `${BASE}/${p.storeid}/open`, 'POST', 'Store re-opened');

export const useBanStore = () =>
  useStoreAction<{ storeid: number; reason: string }>((p) => `${BASE}/${p.storeid}/ban`, 'POST', 'Store banned');

export const useUnbanStore = () =>
  useStoreAction<WithReason>((p) => `${BASE}/${p.storeid}/ban`, 'DELETE', 'Ban lifted');

export const useDeleteStore = () =>
  useStoreAction<WithReason>((p) => `${BASE}/${p.storeid}`, 'DELETE', 'Store deleted');

export const useTransferStore = () =>
  useStoreAction<{ storeid: number; uid?: number | null; oid?: number | null; reason?: string }>(
    (p) => `${BASE}/${p.storeid}/transfer`, 'PUT', 'Ownership transferred',
  );

// ── Plan & payouts ─────────────────────────────────────────────────────────

export const useSetPlanExempt = () =>
  useStoreAction<{ storeid: number; plan_exempt: boolean; reason?: string }>(
    (p) => `${BASE}/${p.storeid}/plan-exempt`, 'PUT',
    (p) => (p.plan_exempt ? 'Plan exemption granted' : 'Plan exemption revoked'),
  );

/** Override plan fields. Send only what changes; `plid: null` clears the plan. */
export const useUpdateStorePlan = () =>
  useStoreAction<{
    storeid: number;
    plid?: number | null;
    plan_tier?: string | null;
    date_plan_expires?: string | null;
    reason?: string;
  }>((p) => `${BASE}/${p.storeid}/plan`, 'PUT', 'Plan updated');

/** `commission_rate: null` clears the override; a rate requires a reason. */
export const useUpdateStoreCommission = () =>
  useStoreAction<{ storeid: number; commission_rate: number | null; reason?: string }>(
    (p) => `${BASE}/${p.storeid}/commission`, 'PUT',
    (p) => (p.commission_rate == null ? 'Commission override cleared' : 'Commission override set'),
  );

export const useSetPayoutHold = () =>
  useStoreAction<{ storeid: number; hold: boolean; reason?: string }>(
    (p) => `${BASE}/${p.storeid}/payout/hold`, 'PUT',
    (p) => (p.hold ? 'Payouts held' : 'Payout hold released'),
  );

// ── Profile ────────────────────────────────────────────────────────────────

export const useSetStoreSlug = () =>
  useStoreAction<{ storeid: number; slug: string; reason?: string }>((p) => `${BASE}/${p.storeid}/slug`, 'PUT', 'Handle updated');

export const useUnlockStoreSlug = () =>
  useStoreAction<WithReason>((p) => `${BASE}/${p.storeid}/slug/lock`, 'DELETE', 'Handle unlocked');

export interface StoreProfileUpdate {
  name?: string;
  tagline?: string | null;
  description?: string | null;
  description_short?: string | null;
  country_iso2?: string | null;
  locale?: string | null;
  lid?: number | null;
  color_scheme?: string | null;
}

export const useUpdateStoreProfile = () =>
  useStoreAction<{ storeid: number; reason?: string } & StoreProfileUpdate>(
    (p) => `${BASE}/${p.storeid}/profile`, 'PUT', 'Profile saved',
  );

/**
 * Update a store's `store_settings` row (`PUT /<id>/settings`). Sends only the
 * changed fields plus `storeid`.
 */
export const useUpdateStoreSettings = () =>
  useStoreAction<{ storeid: number; reason?: string } & Partial<StoreSettingsRow>>(
    (p) => `${BASE}/${p.storeid}/settings`, 'PUT', 'Settings saved',
  );

// ── Listings & staff ───────────────────────────────────────────────────────

export const useModerateStoreListing = () =>
  useStoreAction<{ storeid: number; eid: number } & StoreListingModeration>(
    (p) => `${BASE}/${p.storeid}/listings/${p.eid}/moderate`, 'PUT', 'Listing updated',
  );

/** Grant or deny a set of store permission codes for one staff member. */
export const useSetStoreStaffPermissions = () =>
  useStoreAction<{ storeid: number; uid: number; permissions: string[]; granted: boolean; reason?: string }>(
    (p) => `${BASE}/${p.storeid}/permissions/users`, 'PUT',
    (p) => (p.granted ? 'Permissions granted' : 'Permissions denied'),
  );
